import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ContactProvider } from './contexts/ContactContext';
import { SidebarProvider } from './contexts/SidebarContexts';
import Landing from './pages/Landing';
import Dashboard from './pages/app/home/Dashboard';
import Inventory from './pages/app/tool/Inventory';
import Reports from './pages/app/accounting/Reports';
import Cashier from './pages/app/home/Cashier';
import Login from './pages/app/Login';
import Logout from './components/authentication/Logout';
import Signup from './pages/app/Signup';
import History from './pages/app/accounting/History';
import Pricing from './pages/Pricing';
import NotFound from './pages/NotFound';
import ProtectedLayout from './pages/app/protected/ProtectedLayout';
import Footer from './components/Footer';
import ForgotPassword from './components/authentication/ForgotPassword';
import MyAccount from './components/settings/MyAccount';
import CompanyProfile from './components/settings/CompanyProfile';
import CompanySettings from './components/settings/CompanySettings';
import Billing from './components/settings/Billing';
import FileManager from './components/settings/FileManager';
import Backup from './components/settings/Backup';
import InvoiceSummary from './components/reports/invoice-summary';
import SalesByProduct from './components/reports/sales-by-product';
import SalesByDay from './components/reports/sales-by-day';
import SalesByPaymentMethod from './components/reports/sales-by-payment';
import EndOfDayStatements from './components/reports/end-of-day';
import TopSellingProducts from './components/reports/top-selling-products';
import CancelledOrders from './components/reports/cancelled-orders';
import TaxSummary from './components/reports/tax-summary';
import ProfitLost from './components/reports/profit-lost';
import ProductView from './pages/app/home/ProductView';
import SalesByCashier from './components/reports/sales-by-cashier';
import Online from './pages/app/display/OnlineOrder';
import PickupDeliveryStatus from './pages/app/display/PickupDeliveryStatus';
import QueueDisplay from './pages/app/display/QueueDisplay';
import ReleaseNote from './pages/ReleaseNote';
import PublicReceipts from './pages/publicReceipts';
import StagingLogin from './pages/StagingLogin';
import StagingBanner from './components/staging/StagingBanner';
import MaintenanceNotice from './components/staging/MaintenanceNotice';
import AdminRoutes from './admin/index';
import useIdleLogout from './hooks/useIdleLogout';
import Accounting from './pages/app/accounting/Accounting';
import OnlineOrdering from './pages/OnlineOrdering';
import MoneyIn from './components/accounting/account/MoneyIn';
import MoneyOut from './components/accounting/account/MoneyOut';
import Journal from './components/accounting/account/Journal';
import Members from './components/staff/Members';
import CustomerDisplay from './pages/app/display/CustomerDisplay';
import CompanyReport from './components/reports/comp-report';

function App() {
  const [authenticated, setAuthenticated] = useState(true);

  const isStaging = window.location.hostname.includes('staging');
  const isAuthed = sessionStorage.getItem('staging_auth') === 'true';

  useEffect(() => {
    if (isStaging && !isAuthed) setAuthenticated(false);
  }, []);

  const { showWarning, cancelWarning } = useIdleLogout({
    onLogout: () => {
      sessionStorage.removeItem('staging_auth');
      setAuthenticated(false);
      window.location.reload();
    },
    isAuthed,
  });

  if (!authenticated) {
    return <StagingLogin onAuthenticated={() => setAuthenticated(true)} />;
  }

  return (
    <AuthProvider>
      <ContactProvider>
        <SidebarProvider>
          <Router>
            <div className="min-h-screen flex flex-col">
              <StagingBanner />
              <MaintenanceNotice 
                startTime="2025-09-29T12:00:00"
                endTime="2025-09-29T12:30:00"
                message="We're updating our system."
                enabled={false}
                productionOnly={false}
              />
              {showWarning && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]">
                  <div className="bg-white p-6 rounded shadow-md text-center">
                    <h2 className="text-lg font-bold mb-2 text-red-600">You are inactive</h2>
                    <p>You will be logged out in 10 seconds due to inactivity.</p>
                    <button
                      onClick={() => {
                        cancelWarning();
                      }}
                      className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      Stay Logged In
                    </button>
                  </div>
                </div>
              )}
              <Routes>
                <Route path="/" element={<><Landing /><Footer/></>} />
                <Route path="/login" element={<><Login /><Footer/></>} />
                <Route path="/signup" element={<><Signup /><Footer/></>} />
                <Route path="/pricing" element={<><Pricing /><Footer/></>} />
                <Route path="/release-note" element={<><ReleaseNote /><Footer/></>} />
                <Route path="/forgot-password" element={<><ForgotPassword/><Footer/></>} />
                <Route path="/publicReceipts/:id" element={<><PublicReceipts/><Footer/></>} />
                <Route 
                  path="/online-ordering/:companyId"
                  element={
                    <div className="min-h-screen bg-white">
                      <OnlineOrdering />
                    </div>
                  }
                />
                <Route element={<ProtectedLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/cashier" element={<Cashier />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/logout" element={<Logout />} />
                  <Route path="/my-account" element={<MyAccount />} />
                  <Route path="/company-profile" element={<CompanyProfile />} />
                  <Route path="/company-settings" element={<CompanySettings />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/file-manager" element={<FileManager />} />
                  <Route path="/backup" element={<Backup />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/invoice-summary" element={<InvoiceSummary />} />
                  <Route path="/sales-by-product" element={<SalesByProduct />} />
                  <Route path="/sales-by-day" element={<SalesByDay />} />
                  <Route path="/sales-by-payment" element={<SalesByPaymentMethod />} />
                  <Route path="/end-of-day-statements" element={<EndOfDayStatements />} />
                  <Route path="/top-selling-products" element={<TopSellingProducts />} />
                  <Route path="/cancelled-orders" element={<CancelledOrders />} />
                  <Route path="/tax-summary" element={<TaxSummary />} />
                  <Route path="/profit-lost" element={<ProfitLost />} />
                  <Route path="/product-view" element={<ProductView />} />
                  <Route path="/sales-by-cashier" element={<SalesByCashier />} />
                  <Route path="/online" element={<Online />} />
                  <Route path="/pickup-status" element={<PickupDeliveryStatus />} />
                  <Route path="/queue-display" element={<QueueDisplay />} />
                  <Route path="/accounting" element={<Accounting />} />
                  <Route path="/money-in" element={<MoneyIn />} />
                  <Route path="/money-out" element={<MoneyOut />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/members" element={<Members />} />
                  <Route path="/customer-display" element={<CustomerDisplay />} />
                  <Route path="/company-report" element={<CompanyReport />} />
                </Route>
                <Route path="/admin/*" element={<AdminRoutes />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </Router>
        </SidebarProvider>
      </ContactProvider>
    </AuthProvider>
  );
}

export default App;
